import { Empty, message, Pagination, Select, Spin } from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "~/hooks/use-debounce";
import { SortByField, useGetMyInventories, useGetMyInventory } from "~/services/inventory";
import { CheckType, ContainerType, InventoryStatusCode, PackingTypes, SortOrder } from "~/utils/constants";
import {
  RESPONSIVE_CLASSES,
  getResponsivePagination,
} from "~/utils/responsive";
import InventoryFilters from "./inventory-filters";
import { CheckSquare, Square, Package, Truck, FileText, Plus, Upload, Image as ImageIcon } from "lucide-react";
import { TInventory } from "~/types/inventory";
import { capitalize, formatDimensions } from "~/utils";
import { RequestServiceSidebar } from "../overview/request-service-sidebar";
import { InventoryDetailSidebar } from "./inventory-detail-sidebar";
import { ExportModal, ExportType } from "./export-modal";
import { exportToCSV, exportToPDF, exportPhotoList } from "./export-utils";
import { CreateInventorySidebar } from "./create-inventory-sidebar";
import { ImportInventoryModal } from "./import-inventory-modal";
import "./inventory.css";

export const ClientInventory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState<number>(10);
  const [filterStatus, setFilterStatus] = useState<InventoryStatusCode | "all">("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [search] = useDebounceValue(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortByField>(SortByField.INVENTORY_REF);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [selectedInventory, setSelectedInventory] = useState<TInventory | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  // Store full inventory data for selected items (to preserve across pages)
  const [selectedItemsMap, setSelectedItemsMap] = useState<Map<string, TInventory>>(new Map());
  const [isRequestServiceSidebarOpen, setIsRequestServiceSidebarOpen] = useState(false);
  const [isCreateInventorySidebarOpen, setIsCreateInventorySidebarOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);

  const {
    data: inventory,
    isLoading,
    error: inventoryError,
  } = useGetMyInventories({
    checkParentId: CheckType.CHECK,
    search,
    status: filterStatus === "all" ? undefined : filterStatus,
    page,
    pageSize,
    sortBy,
    sortOrder,
  });

  // Get item ID and search term from URL params
  const itemIdFromUrl = searchParams.get("item");
  const searchFromUrl = searchParams.get("search");

  // Fetch specific inventory item when URL param is present
  const { data: linkedInventory } = useGetMyInventory(itemIdFromUrl || undefined);

  // Handle search parameter from URL
  useEffect(() => {
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
      setPage(1);
      // Remove search param from URL after setting it
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("search");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFromUrl, searchParams, setSearchParams]);

  // Handle URL param for opening specific inventory item
  useEffect(() => {
    if (linkedInventory) {
      setSelectedInventory(linkedInventory);
    }
  }, [linkedInventory]);

  useEffect(() => {
    if (inventoryError) {
      message.error("Failed to load inventory data. Please try again later.");
    }
  }, [inventoryError]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const handleSort = (field: SortByField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC);
    } else {
      setSortBy(field);
      setSortOrder(SortOrder.ASC);
    }
  };

  const handleSelectInventory = (item: TInventory | null) => {
    setSelectedInventory(item);
    // Update URL param
    if (item) {
      searchParams.set("item", item.id);
    } else {
      searchParams.delete("item");
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleCloseSidebar = () => {
    handleSelectInventory(null);
  };

  const toggleSelection = (id: string, item?: TInventory) => {
    setSelectedItemIds((prev) => {
      if (prev.includes(id)) {
        // Remove from map when deselecting
        setSelectedItemsMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.delete(id);
          return newMap;
        });
        return prev.filter((itemId) => itemId !== id);
      } else {
        // Add to map when selecting
        if (item) {
          setSelectedItemsMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.set(id, item);
            return newMap;
          });
        }
        return [...prev, id];
      }
    });
  };

  const toggleSelectAll = () => {
    if (!inventory?.items) return;
    const currentPageIds = inventory.items.map((item) => item.id);
    const allCurrentPageSelected = currentPageIds.every((id) => selectedItemIds.includes(id));

    if (allCurrentPageSelected) {
      // Deselect all items on current page (keep selections from other pages)
      setSelectedItemIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      setSelectedItemsMap((prevMap) => {
        const newMap = new Map(prevMap);
        currentPageIds.forEach((id) => newMap.delete(id));
        return newMap;
      });
    } else {
      // Select all items on current page (add to existing selections)
      setSelectedItemIds((prev) => {
        const newIds = currentPageIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
      setSelectedItemsMap((prevMap) => {
        const newMap = new Map(prevMap);
        inventory.items.forEach((item) => newMap.set(item.id, item));
        return newMap;
      });
    }
  };

  const selectedInventories = useMemo(() => {
    // Return all selected items from the map (preserves selections across pages)
    return Array.from(selectedItemsMap.values());
  }, [selectedItemsMap]);

  // Get all inventories for export (all items or selected items)
  const inventoriesToExport = useMemo(() => {
    if (selectedItemIds.length > 0) {
      return selectedInventories;
    }
    // If no items selected, export all items from current page
    return inventory?.items || [];
  }, [selectedItemIds, selectedInventories, inventory?.items]);

  const handleExport = async (columns: string[], type: ExportType, action: "view" | "download") => {
    try {
      switch (type) {
        case "csv":
          exportToCSV(inventoriesToExport, columns, action);
          break;
        case "pdf":
          exportToPDF(inventoriesToExport, columns, action);
          break;
        case "photo-list":
          await exportPhotoList(inventoriesToExport, columns, action);
          break;
      }
      setIsExportModalOpen(false);
      message.success(`Export ${action === "view" ? "opened" : "downloaded"} successfully`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export. Please try again.");
    }
  };

  const getStatusColor = (status?: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "bg-green-100 text-green-800";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "bg-blue-100 text-blue-800";
      case InventoryStatusCode.NO_POSSESSION:
        return "bg-orange-100 text-orange-800";
      case InventoryStatusCode.RELEASED:
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatStatusText = (status?: InventoryStatusCode) => {
    const statusText = capitalize(status || "");
    if (statusText === "No Possession") {
      return "No AHC Possession";
    }
    return statusText;
  };

  const formatPackingType = (packingType?: PackingTypes, containerType?: ContainerType) => {
    if (containerType) {
      const containerMap: Record<ContainerType, string> = {
        [ContainerType.CRATE]: "Crate",
        [ContainerType.PACKAGE]: "Package",
        [ContainerType.PIECE]: "Piece",
        [ContainerType.T_Frame]: "T Frame",
        [ContainerType.SLIPCASE]: "Slipcase",
        [ContainerType.BOX]: "Box",
        [ContainerType.CUSTOM]: "Custom",
        [ContainerType.SLATCRATE]: "Slat Crate",
        [ContainerType.CBIN]: "C-Bin",
        [ContainerType.PALLET]: "Pallet",
      };
      return containerMap[containerType] || containerType;
    }
    if (!packingType) return null;
    return capitalize(packingType.replace(/_/g, " "));
  };

  const renderSortIndicator = (field: SortByField) => {
    if (sortBy !== field) return null;
    return (
      <span className="ml-1 text-xs">
        {sortOrder === SortOrder.ASC ? "↑" : "↓"}
      </span>
    );
  };

  // Helper function to get client name
  const getClientName = (client: TInventory['client']) => {
    if (client.companyName) {
      return client.companyName;
    }
    const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
    return fullName || "Unknown";
  };

  // Helper function to render title with sub-client name if applicable
  const renderTitle = (item: TInventory) => {
    const baseTitle = item.title || "";
    if (item.client?.isSubClient) {
      const subClientName = getClientName(item.client);
      return (
        <>
          {baseTitle}
          {baseTitle ? " " : ""}
          <span className="font-bold italic">--{subClientName}--</span>
        </>
      );
    }
    return baseTitle;
  };

  const isSidebarOpen = selectedInventory !== null;
  const hasAnySidebarOpen = isSidebarOpen || isCreateInventorySidebarOpen || isRequestServiceSidebarOpen;

  return (
    <div className="flex h-full bg-[#f2f2f2]">
      {/* Main Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          hasAnySidebarOpen ? "w-[calc(100%-520px)]" : "w-full"
        }`}
      >
        <div className={RESPONSIVE_CLASSES.container}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <InventoryFilters
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setPage={setPage}
            />

            {/* Pagination Controls */}
            {inventory && inventory.items.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-neutral-600 text-sm">Show:</span>
                  <Select
                    value={pageSize}
                    onChange={(value) => setPageSize(value)}
                    className="w-20"
                    size="small"
                  >
                    <Select.Option value={10}>10</Select.Option>
                    <Select.Option value={20}>20</Select.Option>
                    <Select.Option value={50}>50</Select.Option>
                  </Select>
                </div>
                <Pagination
                  {...getResponsivePagination()}
                  total={inventory.meta.totalItems}
                  current={page}
                  pageSize={pageSize}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>

          {/* Action Bar */}
          {(selectedItemIds.length > 0 || (inventory && inventory.items.length > 0)) && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 p-3 bg-white border border-[#e5e5e5] rounded-lg">
              <div className="flex items-center gap-4">
                {selectedItemIds.length > 0 && (
                  <span className="text-sm font-bold uppercase tracking-wide text-[#1a1a2e]">
                    {selectedItemIds.length} item{selectedItemIds.length > 1 ? "s" : ""} selected
                  </span>
                )}
                {selectedItemIds.length === 0 && inventory && inventory.items.length > 0 && (
                  <span className="text-sm font-bold uppercase tracking-wide text-[#1a1a2e]">
                    {inventory.meta.totalItems} item{inventory.meta.totalItems !== 1 ? "s" : ""} under management
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedItemIds.length > 0 && (
                  <button
                    onClick={() => setIsRequestServiceSidebarOpen(true)}
                    className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-4 py-2 font-bold text-xs uppercase tracking-wide hover:opacity-90 transition-all flex items-center gap-2"
                  >
                    <Truck size={16} />
                    Request Service
                  </button>
                )}
                <button
                  onClick={() => setIsCreateInventorySidebarOpen(true)}
                  className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-4 py-2 font-bold text-xs uppercase tracking-wide hover:opacity-90 transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <Plus size={16} />
                  Create Inventory
                </button>
                <button
                  onClick={() => setIsImportModalOpen(true)}
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-2 font-bold text-xs uppercase tracking-wide hover:bg-[#fafafa] transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <Upload size={16} />
                  Import Inventory
                </button>
                <button
                  onClick={() => {
                    if (inventoriesToExport.length === 0) {
                      message.warning("No items to export. Please select items or ensure there are items in the list.");
                      return;
                    }
                    setIsExportModalOpen(true);
                  }}
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-2 font-bold text-xs uppercase tracking-wide hover:bg-[#fafafa] transition-all flex items-center gap-2"
                >
                  <FileText size={16} />
                  Export
                </button>
                <button
                  onClick={() => setShowPhotos(!showPhotos)}
                  className={`border rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wide transition-all flex items-center gap-2 ${
                    showPhotos
                      ? "border-[#1a1a2e] bg-[#1a1a2e] text-white"
                      : "border-[#e5e5e5] bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                  }`}
                >
                  <ImageIcon size={16} />
                  Photos
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center p-8 sm:p-12">
              <Spin size="large" tip="Loading inventory..." />
            </div>
          ) : (
            <>
              {inventory && inventory.items.length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="hidden lg:block border border-[#e5e5e5] rounded-lg overflow-hidden bg-white">
                    <table className="w-full">
                      <thead className="bg-[#fafafa] text-left text-xs text-[#1a1a2e] border-b border-[#e5e5e5]">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <button
                              onClick={toggleSelectAll}
                              className="flex items-center justify-center"
                            >
                              {inventory.items.length > 0 &&
                              inventory.items.every((item) => selectedItemIds.includes(item.id)) ? (
                                <CheckSquare size={16} className="text-blue-500" />
                              ) : (
                                <Square size={16} className="text-gray-400" />
                              )}
                            </button>
                          </th>
                          {showPhotos && <th className="px-4 py-3 w-16">Photo</th>}
                          <th
                            className="px-4 py-3 cursor-pointer hover:text-blue-600"
                            onClick={() => handleSort(SortByField.INVENTORY_REF)}
                          >
                            Ref # {renderSortIndicator(SortByField.INVENTORY_REF)}
                          </th>
                          <th className="px-4 py-3">Client Ref #</th>
                          <th
                            className="px-4 py-3 cursor-pointer hover:text-blue-600"
                            onClick={() => handleSort(SortByField.TITLE)}
                          >
                            Title {renderSortIndicator(SortByField.TITLE)}
                          </th>
                          <th
                            className="px-4 py-3 cursor-pointer hover:text-blue-600"
                            onClick={() => handleSort(SortByField.ARTIST_NAME)}
                          >
                            Artist {renderSortIndicator(SortByField.ARTIST_NAME)}
                          </th>
                          <th className="px-4 py-3 w-24">Medium</th>
                          <th className="px-4 py-3 w-40">Dimensions</th>
                          <th className="px-4 py-3">Packing Type</th>
                          <th
                            className="px-4 py-3 cursor-pointer hover:text-blue-600"
                            onClick={() => handleSort(SortByField.STATUS)}
                          >
                            Status {renderSortIndicator(SortByField.STATUS)}
                          </th>
                          <th className="px-4 py-3">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {inventory.items.map((item) => (
                          <tr
                            key={item.id}
                            className="hover:bg-[#fafafa] cursor-pointer border-b border-[#e5e5e5] bg-white"
                            onClick={() => handleSelectInventory(item)}
                          >
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSelection(item.id, item);
                                }}
                                className="flex items-center justify-center"
                              >
                                {selectedItemIds.includes(item.id) ? (
                                  <CheckSquare size={16} className="text-blue-500" />
                                ) : (
                                  <Square size={16} className="text-gray-400" />
                                )}
                              </button>
                            </td>
                            {showPhotos && (
                              <td className="px-4 py-2">
                                {item.images && item.images.length > 0 ? (
                                  <img
                                    src={item.images[0].url}
                                    alt={item.title || ""}
                                    className="w-10 h-10 object-cover rounded border border-[#e5e5e5]"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded border border-[#e5e5e5] bg-[#fafafa] flex items-center justify-center">
                                    <ImageIcon size={14} className="text-gray-300" />
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm font-medium">{item.inventoryRef}</td>
                            <td className="px-4 py-3 text-sm">{item.clientInternalRef || "-"}</td>
                            <td className="px-4 py-3 font-medium">{renderTitle(item)}</td>
                            <td className="px-4 py-3 text-sm">{item.artistName || "-"}</td>
                            <td className="px-4 py-3 text-sm w-24 truncate" title={item.medium || undefined}>
                              {item.medium && item.medium.length > 20
                                ? `${item.medium.substring(0, 20)}...`
                                : item.medium || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm w-40">{formatDimensions(item)}</td>
                            <td className="px-4 py-3 text-sm">
                              {formatPackingType(item.packingType, item.containerType) || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(item.status)}`}>
                                {formatStatusText(item.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.status === InventoryStatusCode.AVAILABLE_ON_HAND
                                ? item.location?.name || "In Storage"
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile/Tablet List */}
                  <div className="lg:hidden space-y-2">
                    {inventory.items.map((item) => (
                      <div
                        key={item.id}
                        className="p-4 border border-[#e5e5e5] rounded-lg cursor-pointer transition-colors bg-white hover:bg-[#fafafa]"
                        onClick={() => handleSelectInventory(item)}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelection(item.id, item);
                            }}
                            className="mt-1"
                          >
                            {selectedItemIds.includes(item.id) ? (
                              <CheckSquare size={18} className="text-blue-500" />
                            ) : (
                              <Square size={18} className="text-gray-400" />
                            )}
                          </button>
                          {showPhotos && (
                            <div className="flex-shrink-0">
                              {item.images && item.images.length > 0 ? (
                                <img
                                  src={item.images[0].url}
                                  alt={item.title || ""}
                                  className="w-14 h-14 object-cover rounded border border-[#e5e5e5]"
                                />
                              ) : (
                                <div className="w-14 h-14 rounded border border-[#e5e5e5] bg-[#fafafa] flex items-center justify-center">
                                  <ImageIcon size={18} className="text-gray-300" />
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-gray-900">{renderTitle(item)}</p>
                                <p className="text-sm text-gray-600">{item.artistName || "Unknown artist"}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${getStatusColor(item.status)}`}>
                                {formatStatusText(item.status)}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span>Ref: {item.inventoryRef}</span>
                              <span>Client Ref: {item.clientInternalRef || "-"}</span>
                              <span>{formatDimensions(item)}</span>
                              {item.status === InventoryStatusCode.AVAILABLE_ON_HAND && (
                                <span>Location: {item.location?.name || "In Storage"}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Instruction when no sidebar */}
                  {!isSidebarOpen && (
                    <div className="mt-6 p-6 bg-white border border-dashed border-[#e5e5e5] rounded-lg text-center">
                      <Package size={32} className="mx-auto mb-2 text-neutral-400" />
                      <p className="text-neutral-600 font-medium">Select an item to view details</p>
                      <p className="text-sm text-neutral-500">
                        Click on any inventory item to see detailed information in the sidebar
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <Empty
                  className="p-8"
                  description="No inventory items found matching your filters."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </>
          )}

        </div>
      </div>

      {/* Inventory Detail Sidebar */}
      {isSidebarOpen && (
        <div
          className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] shadow-lg transition-all duration-300 ease-in-out z-50 overflow-visible ${
            isRequestServiceSidebarOpen || isCreateInventorySidebarOpen
              ? "w-[600px] translate-x-[420px]"
              : "w-[600px] translate-x-0"
          }`}
          style={{ maxWidth: 'calc(100vw - 0px)' }}
        >
          <InventoryDetailSidebar
            inventory={selectedInventory}
            onClose={handleCloseSidebar}
          />
        </div>
      )}

      {/* Create Inventory Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-visible ${
          isCreateInventorySidebarOpen
            ? "w-[420px] translate-x-0"
            : "w-[420px] translate-x-full"
        }`}
      >
        <CreateInventorySidebar
          open={isCreateInventorySidebarOpen}
          onClose={() => {
            setIsCreateInventorySidebarOpen(false);
          }}
        />
      </div>

      {/* Overlay for mobile */}
      {(isSidebarOpen || isCreateInventorySidebarOpen) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => {
            if (isSidebarOpen) handleCloseSidebar();
            if (isCreateInventorySidebarOpen) setIsCreateInventorySidebarOpen(false);
          }}
        />
      )}

      {/* Request Service Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-visible ${
          isRequestServiceSidebarOpen ? "w-[420px] translate-x-0" : "w-[420px] translate-x-full"
        }`}
      >
        <RequestServiceSidebar
          open={isRequestServiceSidebarOpen}
          onClose={() => {
            setIsRequestServiceSidebarOpen(false);
            setSelectedItemIds([]);
            setSelectedItemsMap(new Map());
          }}
          preSelectedInventoryIds={selectedItemIds}
        />
      </div>

      {/* Export Modal */}
      <ExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportType={"csv"}
        inventories={inventoriesToExport}
        onExport={handleExport}
      />

      {/* Import Inventory Modal */}
      <ImportInventoryModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          // Optionally refresh inventory list
        }}
      />
    </div>
  );
};
